import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
} from 'react';

import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

import _ from 'lodash';
import { toast } from 'sonner';
import { v4 as newUuid } from 'uuid';
import { analytics } from '@client/analytics';
import { inviteApi } from '@/features/invite-client';
import { selectSessionAdvisor, fetchClientsForAdvisorByAdvisorIdRequest } from '@client/model';
import { getIntroIntakeFormApiEndPoint, getClientOnboardingUrl, isValidEmailFormat } from '@shared';

export type TabType = 'email' | 'link' | 'fill-form';
export type StepType =
  | 'method'
  | 'email'
  | 'customize-email'
  | 'email-additional-settings'
  | 'link'
  | 'link-additional-settings'
  | 'quick-intake';

type ShareResultsOption = 'yes' | 'no';

export interface Tag {
  value: string;
  isValid: boolean;
  error: string;
  contactId?: string;
  firstName?: string;
  lastName?: string;
}

export interface InviteClientConfig {
  email: string;
  firstName: string;
  lastName: string;
  bulkEmails: Tag[];
  isBulkMode: boolean;
  isComplianceConfirmed: boolean;
  emailBody: string;
  shareResults: ShareResultsOption;
  referralSource?: string | undefined;
  clientRelationship?: ClientRelationshipStage | undefined;
  redtailContactId?: string | undefined;
}

export interface InviteClientStoreType {
  inviteClientConfig: InviteClientConfig;
  showEmailWarningModal: boolean;
  showAdditionalSettings: boolean;
  isEmailValid: boolean;
  isAdvisorEmail: boolean;
  canContinueSingle: boolean;
  validBulkEmails: Tag[];
  canContinueBulk: boolean;
  isLoading: boolean;
  isComplete: boolean;

  setInviteClientConfig: Dispatch<SetStateAction<InviteClientConfig>>;
  setShowEmailWarningModal: Dispatch<SetStateAction<boolean>>;
  setShowAdditionalSettings: Dispatch<SetStateAction<boolean>>;

  handleConnectEmail: () => void;
  handleClose: () => void;
  generateIntakeFormLink: (surveyId: string, contactId?: string) => string;
  sendInvites: (isBulk: boolean) => void;
  handleSendEmailAnyway: (isBulk: boolean) => void;
  handleSendEmail: (isBulk: boolean) => void;
  generateShareableLink: () => string;
  handleConfigChange: (
    field: keyof InviteClientConfig,
    value: string | boolean | Tag[] | undefined,
  ) => void;
  handleClearBulkEmails: () => void;
}

export const EMAIL_WARNING_DISMISSED_KEY = 'emailDeliverabilityWarningDismissed';
export const DEFAULT_EMAIL_BODY = `As one of the most devastating events in retirement, it means a lot to me to prepare my clients for the possibility of a long-term care event.

I use Waterlily to build personalized long-term care plans for clients. It works by predicting your unique future LTC needs, building a care plan for you, and then I can analyze different ways we could fund these costs to find the options that make the most sense for you.

The first step is to complete the intake form below (it's about 5 minutes long) and then we get the predictions that we can work with and build from there.`;

export const InviteClientContext = createContext<InviteClientStoreType | null>(null);

export function useInviteClientStore() {
  const context = useContext(InviteClientContext);

  if (!context) {
    throw new Error('`useInviteClientStore` should be used inside the `<InviteClientProvider />`');
  }

  return context;
}

export function useInviteClientTrigger() {
  const [searchParams, setSearchParams] = useSearchParams();

  const isInviteClientOpen = searchParams.get('inviteClient') === 'true';
  const inviteClientTab = searchParams.get('inviteClientType') as TabType;
  const initialBulkEmailsParam = searchParams.get('initialBulkEmails');
  const initialBulkEmails: Tag[] = initialBulkEmailsParam
    ? JSON.parse(decodeURIComponent(initialBulkEmailsParam))
    : [];
  const redtailContactId = searchParams.get('redtailContactId') ?? undefined;

  const setInviteClientParams = useCallback(
    (tab?: TabType, bulkEmails?: Tag[], contactId?: string) => {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);

        newParams.set('inviteClient', 'true');
        newParams.set('inviteClientType', tab || 'email');

        if (bulkEmails && bulkEmails.length > 0) {
          newParams.set('initialBulkEmails', encodeURIComponent(JSON.stringify(bulkEmails)));
        }

        if (contactId) {
          newParams.set('redtailContactId', contactId);
        }

        return newParams;
      });
    },
    [setSearchParams],
  );

  const clearInviteClientParams = useCallback(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      newParams.delete('inviteClient');
      newParams.delete('inviteClientType');
      newParams.delete('initialBulkEmails');
      newParams.delete('redtailContactId');

      return newParams;
    });
  }, [setSearchParams]);

  const setInviteClientTab = useCallback(
    (tab: TabType) => {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('inviteClientType', tab);
        return newParams;
      });
    },
    [setSearchParams],
  );

  return {
    isInviteClientOpen,
    inviteClientTab,
    initialBulkEmails,
    redtailContactId,
    setInviteClientParams,
    setInviteClientTab,
    clearInviteClientParams,
  };
}

export interface InviteClientProviderProps {
  children: ReactNode;
  onInviteSuccess?: () => void;
  onClose?: () => void;
  prefillWithAdvisor?: boolean;
  skipEmailWarning?: boolean;
}

export function useInviteClientProvider({
  onInviteSuccess,
  onClose,
  prefillWithAdvisor = false,
  skipEmailWarning = false,
}: Omit<InviteClientProviderProps, 'children'>) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const advisor = useSelector(selectSessionAdvisor);
  const { initialBulkEmails, redtailContactId, clearInviteClientParams } = useInviteClientTrigger();
  const { flags } = useFeatureFlags();
  const useFormProvider = flags.useFormProvider ?? false;

  const [showEmailWarningModal, setShowEmailWarningModal] = useState(false);
  const [showAdditionalSettings, setShowAdditionalSettings] = useState(false);

  const defaultInviteClientConfig: InviteClientConfig = {
    email: '',
    firstName: prefillWithAdvisor ? (advisor.advisorFirstName ?? '') : '',
    lastName: prefillWithAdvisor ? (advisor.advisorLastName ?? '') : '',
    bulkEmails: [],
    isBulkMode: false,
    isComplianceConfirmed: false,
    emailBody: DEFAULT_EMAIL_BODY,
    shareResults: advisor.sendClientResultsLinkSetting ? 'yes' : 'no',
  };

  const [inviteClientConfig, setInviteClientConfig] =
    useState<InviteClientConfig>(defaultInviteClientConfig);

  useEffect(() => {
    const isSingleFromBulk = initialBulkEmails.length === 1;

    if (isSingleFromBulk) {
      const singleTag = initialBulkEmails[0];

      setInviteClientConfig(currentConfig => ({
        ...currentConfig,
        email: singleTag.value,
        firstName: singleTag.firstName ?? '',
        lastName: singleTag.lastName ?? '',
        redtailContactId: singleTag.contactId ?? redtailContactId,
      }));
    } else if (initialBulkEmails.length > 1) {
      setInviteClientConfig(currentConfig => ({
        ...currentConfig,
        bulkEmails: initialBulkEmails,
        isBulkMode: true,
        redtailContactId,
      }));
    }
  }, [initialBulkEmails, redtailContactId]);

  function fetchClientsForAdvisorOnSuccess() {
    dispatch(
      fetchClientsForAdvisorByAdvisorIdRequest({
        advisorId: advisor.advisorId,
        isInvalidation: true,
      }),
    );
  }

  function resetState() {
    const isSingleFromBulk = initialBulkEmails.length === 1;

    if (isSingleFromBulk) {
      const singleTag = initialBulkEmails[0];
      setInviteClientConfig({
        ...defaultInviteClientConfig,
        email: singleTag.value,
        firstName: singleTag.firstName ?? '',
        lastName: singleTag.lastName ?? '',
        redtailContactId: singleTag.contactId ?? redtailContactId,
      });
    } else {
      setInviteClientConfig({
        ...defaultInviteClientConfig,
        bulkEmails: initialBulkEmails.length > 1 ? initialBulkEmails : [],
        isBulkMode: initialBulkEmails.length > 1,
        redtailContactId,
      });
    }
    inviteMutation.reset();
    batchInviteMutation.reset();
  }

  const handleClose = useCallback(() => {
    resetState();
    dispatch({ type: 'RESUME_SESSION_REFRESH' });
    if (onClose) {
      onClose();
    } else {
      clearInviteClientParams();
    }
  }, [onClose, clearInviteClientParams, dispatch]);

  const inviteMutation = useMutation({
    mutationFn: inviteApi.sendInvite,
    onSuccess: data => {
      if (!data.success) {
        toast.error('Failed to send invite', {
          description: data.error || 'Please try again',
        });
        return;
      }
      toast.success('Invite sent successfully');
      fetchClientsForAdvisorOnSuccess();

      onInviteSuccess ? onInviteSuccess() : handleClose();
    },
    onError: () => {
      toast.error('Failed to send invite', {
        description: 'Please try again or contact support',
      });
    },
  });

  const batchInviteMutation = useMutation({
    mutationFn: inviteApi.sendBatchInvite,
    onSuccess: data => {
      const { errorCount, successCount, errors } = data;
      if (successCount > 0 && errorCount > 0) {
        toast.warning(`Sent ${successCount} invites with ${errorCount} failures`, {
          description: `Failed: ${errors.map(e => e.email).join(', ')}`,
        });
        setInviteClientConfig(currentConfig => ({
          ...currentConfig,
          bulkEmails: currentConfig.bulkEmails.filter(el =>
            errors.map(e => e.email).includes(el.value),
          ),
        }));
        fetchClientsForAdvisorOnSuccess();
        return;
      }
      if (successCount > 0) {
        toast.success(`Successfully sent ${successCount} invites`);
        fetchClientsForAdvisorOnSuccess();
        onInviteSuccess ? onInviteSuccess() : handleClose();
        return;
      }
      toast.error('Failed to send invites', {
        description: 'Please try again or contact support',
      });
    },
    onError: () => {
      toast.error('Failed to send invites', {
        description: 'Please try again or contact support',
      });
    },
  });

  const isEmailValid = isValidEmailFormat(inviteClientConfig.email);
  // Skip advisor self-invite check when FF is on, to allow testing with advisor's own email
  const isAdvisorEmail =
    !flags.advisorClientLogin &&
    advisor.advisorEmail !== undefined &&
    advisor.advisorEmail !== null &&
    inviteClientConfig.email.trim().toLowerCase() === advisor.advisorEmail.trim().toLowerCase();
  const canContinueSingle = inviteClientConfig.email.length > 0 && isEmailValid && !isAdvisorEmail;
  const validBulkEmails = inviteClientConfig.bulkEmails.filter(email => email.isValid);
  const canContinueBulk = validBulkEmails.length > 0 && inviteClientConfig.isComplianceConfirmed;

  const isLoading = inviteMutation.isPending || batchInviteMutation.isPending;
  const isComplete = inviteMutation.isSuccess || batchInviteMutation.isSuccess;

  const hasConnectedEmail = !!(
    (advisor.advisorOauthSettings?.google?.userEmail &&
      advisor.advisorOauthSettings?.google?.createdAtDateTime) ||
    (advisor.advisorOauthSettings?.microsoft?.userEmail &&
      advisor.advisorOauthSettings?.microsoft?.createdAtDateTime)
  );

  function shouldShowEmailDeliverabilityWarning() {
    return localStorage.getItem(EMAIL_WARNING_DISMISSED_KEY) !== 'true';
  }

  useEffect(() => {
    const { firstName } = inviteClientConfig;

    const greeting = firstName ? `Hi ${firstName},\n\n` : '';

    const emailText = `${greeting}As one of the most devastating events in retirement, it means a lot to me to prepare my clients for the possibility of a long-term care event.

I use Waterlily to build personalized long-term care plans for clients. It works by predicting your unique future LTC needs, building a care plan for you, and then I can analyze different ways we could fund these costs to find the options that make the most sense for you.

The first step is to complete the intake form below (it's about 5 minutes long) and then we get the predictions that we can work with and build from there.`;

    setInviteClientConfig(currentConfig => ({
      ...currentConfig,
      emailBody: emailText,
    }));
  }, [inviteClientConfig.firstName]);

  const handleConnectEmail = useCallback(() => {
    setShowEmailWarningModal(false);
    handleClose();
    navigate(`/advisors/${advisor.advisorId}/settings?tab=communication`);
  }, [advisor.advisorId, handleClose, navigate]);

  const generateIntakeFormLink = useCallback(
    (surveyId: string, contactId?: string) => {
      if (useFormProvider) {
        return getClientOnboardingUrl(advisor.advisorId);
      }
      return getIntroIntakeFormApiEndPoint(
        advisor.advisorId,
        inviteClientConfig.shareResults === 'yes',
        surveyId,
        undefined,
        contactId ?? inviteClientConfig.redtailContactId,
      );
    },
    [
      advisor.advisorId,
      inviteClientConfig.shareResults,
      inviteClientConfig.redtailContactId,
      useFormProvider,
    ],
  );

  const sendInvites = useCallback(
    (isBulk: boolean) => {
      analytics.inviteClients({
        clientCount: isBulk ? validBulkEmails.length : 1,
        inviteMethod: isBulk ? 'bulk' : 'single',
      });

      if (!isBulk) {
        const surveyId = newUuid();

        inviteMutation.mutate({
          surveyId,
          createNewClient: true,
          advisorId: advisor.advisorId,
          email: inviteClientConfig.email.trim(),
          clientFirstName: inviteClientConfig.firstName.trim(),
          clientLastName: inviteClientConfig.lastName.trim(),
          advisorProvidedBody: inviteClientConfig.emailBody,
          intakeFormLink: generateIntakeFormLink(surveyId),
          clientRelationshipStage: inviteClientConfig.clientRelationship,
          referralSourceId: inviteClientConfig.referralSource,
          redtailContactId: inviteClientConfig.redtailContactId,
          hasAccessToResults: inviteClientConfig.shareResults === 'yes',
        });
      } else {
        if (!inviteClientConfig.isComplianceConfirmed) return;

        const payload = validBulkEmails.map(emailTag => {
          const surveyId = newUuid();
          return {
            email: emailTag.value.trim(),
            surveyId,
            advisorId: advisor.advisorId,
            intakeFormLink: generateIntakeFormLink(surveyId, emailTag.contactId),
            clientRelationshipStage: inviteClientConfig.clientRelationship,
            referralSourceId: inviteClientConfig.referralSource,
            advisorProvidedBody: inviteClientConfig.emailBody,
            hasAccessToResults: inviteClientConfig.shareResults === 'yes',
          };
        });

        batchInviteMutation.mutate({ intakeFormEmailProps: payload });
      }
    },
    [
      advisor.advisorId,
      inviteClientConfig.email,
      inviteClientConfig.firstName,
      inviteClientConfig.lastName,
      inviteClientConfig.emailBody,
      inviteClientConfig.clientRelationship,
      inviteClientConfig.referralSource,
      inviteClientConfig.isComplianceConfirmed,
      generateIntakeFormLink,
      validBulkEmails,
      inviteMutation,
      batchInviteMutation,
    ],
  );

  const handleSendEmail = useCallback(
    (isBulk: boolean) => {
      if (!skipEmailWarning && !hasConnectedEmail && shouldShowEmailDeliverabilityWarning()) {
        setShowEmailWarningModal(true);
        return;
      }
      sendInvites(isBulk);
    },
    [skipEmailWarning, hasConnectedEmail, sendInvites],
  );

  const handleSendEmailAnyway = useCallback(
    (isBulk: boolean) => {
      setShowEmailWarningModal(false);
      sendInvites(isBulk);
    },
    [sendInvites],
  );

  const handleConfigChange = useCallback(
    (
      field: keyof typeof inviteClientConfig,
      value: (typeof inviteClientConfig)[keyof typeof inviteClientConfig],
    ) => {
      setInviteClientConfig(currentConfig => ({ ...currentConfig, [field]: value }));
    },
    [],
  );

  const handleClearBulkEmails = useCallback(() => {
    setInviteClientConfig(currentConfig => ({
      ...currentConfig,
      bulkEmails: [],
      isComplianceConfirmed: false,
    }));
  }, []);

  const generateShareableLink = useCallback(() => {
    if (useFormProvider) {
      return getClientOnboardingUrl(advisor.advisorId, {
        shareResults: inviteClientConfig.shareResults,
        clientRelationshipStage: inviteClientConfig.clientRelationship,
        customReferralSourceId: inviteClientConfig.referralSource,
      });
    }
    return getIntroIntakeFormApiEndPoint(
      advisor.advisorId,
      inviteClientConfig.shareResults === 'yes',
      undefined,
      inviteClientConfig.referralSource,
    );
  }, [
    advisor.advisorId,
    inviteClientConfig.shareResults,
    inviteClientConfig.clientRelationship,
    inviteClientConfig.referralSource,
    useFormProvider,
  ]);

  return {
    inviteClientConfig,
    showEmailWarningModal,
    showAdditionalSettings,
    isEmailValid,
    isAdvisorEmail,
    canContinueSingle,
    validBulkEmails,
    canContinueBulk,
    isLoading,
    isComplete,

    setInviteClientConfig,
    setShowEmailWarningModal,
    setShowAdditionalSettings,

    handleConnectEmail,
    handleClose,
    generateIntakeFormLink,
    sendInvites,
    handleSendEmailAnyway,
    handleSendEmail,
    generateShareableLink,
    handleConfigChange,
    handleClearBulkEmails,
  };
}
